import React from "react";
import Link from "next/link";
import { SectionTitle, sharedStyles } from "./_shared";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { getBethHabadLabel } from "../utils";
import { isExternalUrl, buildLinkHref, buildMenuUrl } from "../../../components/normalizeMenuUrl";
import {isTravelHomeLinkHidden} from '../../../components/travelHomepage';

const infoTilesStyles = `
.tr-itiles__grid {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(4px, 0.8vw, 10px);
}

.tr-itile {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 8px 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  min-width: 120px;
}
.tr-itile__icon {
  width: 104px; height: 104px;
  border-radius: 24px;
  background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
  color: var(--primary, #1b579e);
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--primary, #1b579e) 18%, white);
  transition: transform .2s ease, box-shadow .2s ease;
}
.tr-itile:hover .tr-itile__icon {
  transform: translateY(-3px);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--primary, #1b579e) 32%, white),
              0 10px 24px -14px color-mix(in srgb, var(--primary, #1b579e) 60%, black);
}
.tr-itile__label {
  font-size: 14px; font-weight: 600;
  color: #1a1a2e;
  text-align: center;
}
`;

// InfoTiles : rangée de tuiles icon + label cliquables, autonome.
// Raccourcis vers les sections clés du site Travel, filtrés selon ce qui
// est réellement disponible dans le context (config + travel data).
export default function InfoTiles({ context }) {
    const { org, config, travel } = context;
    const bethHabadLabel = getBethHabadLabel(travel, context.lang);

    const tiles = [
        (travel?.tourismInfo || config["menu.tourism.active"] !== false) && {
            icon: { name: "IconLuggage", source: "tabler" },
            label: t`Infos voyageur`,
            url: "/travel/tourist-info",
        },
        ((travel?.hotelPlaces || []).length > 0 || travel?.bookingApi?.enabled) && {
            icon: { name: "IconBuildingSkyscraper", source: "tabler" },
            label: t`Hôtels`,
            url: "/travel/hotels",
        },
        (travel?.kosherPlaces || []).length > 0 && {
            icon: { name: "IconToolsKitchen2", source: "tabler" },
            label: t`Manger Cacher`,
            url: "/travel/kosher-places",
        },
        travel?.bethHabad && {
            icon: { name: "IconHome2", source: "tabler" },
            label: bethHabadLabel,
            url: "/travel/beth-chabad",
        },
    ].filter(Boolean).filter(tile => !isTravelHomeLinkHidden(context, tile.url));

    if (tiles.length === 0) return null;

    const resolvedTitle = t`Tout ce qu'il faut pour un séjour cacher à ${org.ville || org.name}`;
    const resolvedHighlight = t`séjour cacher`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + infoTilesStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={resolvedTitle} highlight={resolvedHighlight} />
                <div className="tr-itiles__grid">
                    {tiles.map((tile, i) => {
                        const url = tile.url ? buildMenuUrl(tile.url, org) : null;
                        const isExternal = url && isExternalUrl(url, org);
                        const inner = (
                            <>
                                <div className="tr-itile__icon">
                                    {renderIcon(tile.icon, 52)}
                                </div>
                                <div className="tr-itile__label">{tile.label}</div>
                            </>
                        );
                        if (!url) {
                            return <div key={i} className="tr-itile">{inner}</div>;
                        }
                        if (isExternal) {
                            return <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="tr-itile">{inner}</a>;
                        }
                        return (
                            <Link key={i} href={buildLinkHref(url, org)} legacyBehavior>
                                <a className="tr-itile">{inner}</a>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </>
    );
}
